import { useState, useEffect, useCallback, useContext, useSyncExternalStore } from 'react'
import { useSocket } from '../contexts/SocketContext'
import { AuthContext } from '../contexts/AuthContext'
import { apiPost, apiDelete, apiGet } from '../utils/api'

// ─── Module-level state for cross-component reactivity ──────────────────────
// Same pattern as useAudioAlerts: useSyncExternalStore with a version counter
let _observedChatIds = new Set<string>()
let _observedVersion = 0
const _listeners = new Set<() => void>()

function notifyObservedListeners() {
  _observedVersion++
  _listeners.forEach((l) => l())
}

function subscribeObserved(cb: () => void) {
  _listeners.add(cb)
  return () => {
    _listeners.delete(cb)
  }
}

function getObservedSnapshot() {
  return _observedVersion
}

export function isObservingChat(chatId: string): boolean {
  return _observedChatIds.has(chatId)
}

// ─── Observer type ──────────────────────────────────────────────────────────

export interface Observer {
  id: string
  agent_id: string
  agent_name: string
  agent_role: string
  joined_at: string
}

export interface WhisperMessage {
  id: string
  chat_id: string
  sender_type: 'whisper'
  sender_id: string
  sender_name: string
  sender_role: string
  body: string
  attachments: any[]
  created_at: string
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export const useWhisperMode = (chatId?: string) => {
  const [isObserving, setIsObserving] = useState(false)
  const [observers, setObservers] = useState<Observer[]>([])
  const [whisperMessages, setWhisperMessages] = useState<WhisperMessage[]>([])
  const [loading, setLoading] = useState(false)
  const { socket } = useSocket()
  const auth = useContext(AuthContext)
  const myId = auth?.currentAgent?.id != null ? String(auth.currentAgent.id) : ''

  // Subscribe to module-level observed set for reactivity
  useSyncExternalStore(subscribeObserved, getObservedSnapshot)

  // Fetch observers when chatId changes. If I'M an active observer (e.g. I was
  // invited to whisper), enter whisper mode automatically.
  const fetchObservers = useCallback(async () => {
    if (!chatId) return
    try {
      const data = await apiGet<{ observers: Observer[]; count: number }>(`/chats/${chatId}/observers`)
      const list = data.observers || []
      setObservers(list)
      if (myId && list.some((o) => String(o.agent_id) === myId)) {
        if (!_observedChatIds.has(chatId)) { _observedChatIds.add(chatId); notifyObservedListeners() }
        setIsObserving(true)
      }
    } catch {
      // Silent fail — observers list is non-critical
    }
  }, [chatId, myId])

  // Load whisper history on mount (so the handler + observers see past whispers).
  useEffect(() => {
    if (!chatId) return
    let active = true
    apiGet<{ messages: WhisperMessage[] }>(`/chats/${chatId}/whisper`)
      .then((d) => { if (active) setWhisperMessages(d.messages || []) })
      .catch(() => { /* none / not permitted */ })
    return () => { active = false }
  }, [chatId])

  // Invite another agent to whisper on this chat.
  const inviteWhisper = useCallback(async (agentId: string) => {
    if (!chatId || !agentId) return
    await apiPost(`/chats/${chatId}/invite-whisper`, { agent_id: agentId })
  }, [chatId])

  // Join whisper mode
  const joinWhisper = useCallback(async () => {
    if (!chatId || isObserving) return

    try {
      setLoading(true)
      const data = await apiPost<any>(`/chats/${chatId}/observe`)

      if (data.success) {
        setIsObserving(true)
        _observedChatIds.add(chatId)
        notifyObservedListeners()

        // Join whisper Socket.IO room
        if (socket) {
          socket.emit('join_whisper', { chatId })
        }

        // Load whisper message history
        if (data.whisper_messages) {
          setWhisperMessages(data.whisper_messages)
        }

        // Refresh observers list
        fetchObservers()
      }
    } catch (err) {
      console.error('Failed to join whisper mode:', err)
    } finally {
      setLoading(false)
    }
  }, [chatId, isObserving, socket, fetchObservers])

  // Leave whisper mode
  const leaveWhisper = useCallback(async () => {
    if (!chatId || !isObserving) return

    try {
      await apiDelete(`/chats/${chatId}/observe`)
      setIsObserving(false)
      _observedChatIds.delete(chatId)
      notifyObservedListeners()

      // Leave whisper Socket.IO room
      if (socket) {
        socket.emit('leave_whisper', { chatId })
      }

      setWhisperMessages([])
      setObservers([])
    } catch (err) {
      console.error('Failed to leave whisper mode:', err)
    }
  }, [chatId, isObserving, socket])

  // Send whisper message (text and/or attachments)
  const sendWhisper = useCallback(async (body: string, attachments?: any[]) => {
    if (!chatId || (!body.trim() && !(attachments && attachments.length))) return

    try {
      const msg = await apiPost<WhisperMessage>(`/chats/${chatId}/whisper`, { body, attachments: attachments || [] })
      // Add to local state (socket listener will also add, deduplicate by ID)
      setWhisperMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev
        return [...prev, msg]
      })
    } catch (err) {
      console.error('Failed to send whisper:', err)
    }
  }, [chatId])

  // Socket.IO listeners for whisper events
  useEffect(() => {
    if (!socket || !chatId) return

    const handleWhisperMessage = (msg: WhisperMessage) => {
      const msgChatId = msg.chat_id || (msg as any).chatId
      if (msgChatId !== chatId) return

      setWhisperMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev
        return [...prev, msg]
      })
    }

    const handleObserverJoined = (data: { chatId: string; agentId: string; agentName: string; agentRole: string }) => {
      if (data.chatId !== chatId) return
      fetchObservers()
    }

    const handleObserverLeft = (data: { chatId: string; agentId: string; agentName: string }) => {
      if (data.chatId !== chatId) return
      setObservers((prev) => prev.filter((o) => o.agent_id !== data.agentId))
    }

    socket.on('whisper_message', handleWhisperMessage)
    socket.on('observer_joined', handleObserverJoined)
    socket.on('observer_left', handleObserverLeft)

    return () => {
      socket.off('whisper_message', handleWhisperMessage)
      socket.off('observer_joined', handleObserverJoined)
      socket.off('observer_left', handleObserverLeft)
    }
  }, [socket, chatId, fetchObservers])

  // Check if we're already observing this chat on mount
  useEffect(() => {
    if (chatId && _observedChatIds.has(chatId)) {
      setIsObserving(true)
    } else {
      setIsObserving(false)
      setWhisperMessages([])
    }
  }, [chatId])

  // Fetch observers on mount
  useEffect(() => {
    fetchObservers()
  }, [fetchObservers])

  // Clean up on unmount — leave whisper room if observing this specific chat
  useEffect(() => {
    const currentChatId = chatId
    return () => {
      if (currentChatId && _observedChatIds.has(currentChatId)) {
        _observedChatIds.delete(currentChatId)
        notifyObservedListeners()
        // Notify server we left
        if (socket) {
          socket.emit('leave_whisper', { chatId: currentChatId })
        }
      }
    }
  }, [chatId, socket])

  return {
    isObserving,
    observers,
    whisperMessages,
    loading,
    joinWhisper,
    leaveWhisper,
    sendWhisper,
    inviteWhisper,
    fetchObservers,
    observedChatIds: _observedChatIds,
  }
}
